import React, { useState, useRef, useEffect } from 'react';
import { Table, Typography, Row, Col, Button, Icon, Modal, Input, Tooltip } from 'antd';
import SectionContent from '../../../components/SectionContent';
import SectionTitle from '../../../components/SectionTitle';
import SectionWrapper from '../../../components/SectionWrapper';
import Card, { Meta } from '../../../components/Card';
import CircularLoader from '../../../components/CircularLoader';
import CircleFlag from '../../../components/CircleFlag';
import blueBg from '../../../assets/img/blue-bg.png';
import { getCountryName } from '../../../utils';
import Highlighter from 'react-highlight-words';
import ZeroState from '../../../components/ZeroState';
import { isoCountries } from '../../../utils/isoCountryCodes';
import { Query, Mutation } from '@apollo/client/react/components';
import { GET_LEAGUE_PLAYERS_QUERY } from '../data/queries';

const { Title, Text } = Typography;

const styles = {
    headerImg: {
        height: 120,
        overflow: 'hidden',
        borderRadius: 0,
        background: `linear-gradient(rgba(61, 185, 248, 0.8), rgba(24, 144, 255, 0.1)), url(${blueBg}) 0px 0px no-repeat`,
        // backgroundAttachment: 'fixed',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100%',
        display: 'flex',
        alignItems: 'center',
        paddingLeft: 40,
        paddingRight: 40,
        // backgroundPosition: '0px -40px'
    },
};

export default function DPLeagueStandings(props) {
    const { league, division } = props;
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef(null);

    function handleSearch(selectedKeys, confirm, dataIndex) {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    }

    function handleReset(clearFilters) {
        clearFilters();
        setSearchText('');
    }

    function getColumnSearchProps(dataIndex) {
        return {
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        ref={searchInput}
                        placeholder={`Search ${dataIndex}`}
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                    />
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon="search"
                        size="small"
                        style={{ width: 90, marginRight: 8 }}
                    >
                        Search
                    </Button>
                    <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        Reset
                    </Button>
                </div>
            ),
            filterIcon: (filtered) => <Icon type="search" style={{ color: filtered ? '#1890ff' : undefined }} />,
            onFilter: (value, record) => record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
            onFilterDropdownVisibleChange: (visible) => {
                if (visible) {
                    setTimeout(() => searchInput.current.select());
                }
            },
            render: (text) =>
                searchedColumn === dataIndex ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={text.toString()}
                    />
                ) : (
                    text
                ),
        };
    }

    const columns = [
        {
            title: '#',
            dataIndex: 'index',
            width: 80,
            sorter: (a, b) => {
                return a.index - b.index;
            },
        },
        {
            title: 'Name',
            dataIndex: 'name',
            defaultSortOrder: 'ascend',
            ...getColumnSearchProps('name'),
            sorter: (a, b) => {
                return a.name && a.name.localeCompare(b.name);
            },
            render: (text, record) => {
                return (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        {record.country ? (
                            <React.Fragment>
                                <CircleFlag country={record.country} left={0} />
                                <Text>{text}</Text>
                            </React.Fragment>
                        ) : (
                            <Text>{text}</Text>
                        )}
                    </div>
                );
            },
        },
        // {
        // 	title: 'Divisions',
        // 	dataIndex: 'divisions',
        // 	sorter: (a, b) => {
        // 		return a.divisions.localeCompare(b.divisions, undefined, {
        // 			numeric: true,
        // 			sensitivity: 'base'
        // 		});
        // 	},
        // 	render: (text, record) => <React.Fragment>{text || '-'}</React.Fragment>
        // },

        {
            title: 'Skill Level',
            dataIndex: 'skill_level',
            sorter: (a, b) => a.skill_level - b.skill_level,
            render: (text, record) => <React.Fragment>{text || '-'}</React.Fragment>,
        },
        {
            title: 'Points',
            dataIndex: 'points',
            sorter: (a, b) => a.points - b.points,
            render: (text, record) => <React.Fragment>{text || '-'}</React.Fragment>,
        },
        {
            title: 'Challenges played',
            dataIndex: 'challenges_played',
            sorter: (a, b) => a.challenges_played - b.challenges_played,
            render: (text, record) => <React.Fragment>{text || '-'}</React.Fragment>,
        },
        {
            title: 'Challenges won',
            dataIndex: 'challenges_won',
            sorter: (a, b) => a.challenges_won - b.challenges_won,
            render: (text, record) => <React.Fragment>{text || '-'}</React.Fragment>,
        },
        {
            title: 'Challenges lost',
            dataIndex: 'challenges_lost',
            sorter: (a, b) => a.challenges_lost - b.challenges_lost,
            render: (text, record) => <React.Fragment>{text || '-'}</React.Fragment>,
        },
    ];

    return (
        <SectionWrapper>
            {/* <SectionTitle
				title="Players"
				titleStyle={{ color: '#fff', margin: 0, textTransform: 'uppercase' }}
				height={120}
			/> */}
            <SectionContent padding="20px 40px">
                <Row
                    gutter={24}
                    justify="center"
                    type="flex"
                    // style={{ padding: 24, position: 'relative', marginTop: -120 }}
                >
                    <Col xs={24} sm={props.navType === 'desktop' ? 24 : 22}>
                        <Card title="League standings" bodyStyle={{ padding: 0 }}>
                            <Query
                                query={GET_LEAGUE_PLAYERS_QUERY}
                                fetchPolicy="cache-and-network"
                                notifyOnNetworkStatusChange
                                awaitRefetchQueries
                                variables={{ league_id: league.id }}
                                onCompleted={(data) => {
                                    console.log(data);
                                }}
                            >
                                {({ loading, error, data }) => {
                                    if (loading) return <CircularLoader />;
                                    if (error) return <div style={styles.container}>Error: {error.message}</div>;
                                    if ((data && !data.league_players) || (data && data.league_players.length === 0)) {
                                        return <ZeroState showImage message="No Players have been added yet." style={{ margin: 30 }} {...props} />;
                                    }

                                    const standings =
                                        data.league_players &&
                                        data.league_players.map((item, index) => {
                                            const playerChallengesPlayed =
                                                item.player &&
                                                item.player.player_challenges_played &&
                                                item.player.player_challenges_played.aggregate &&
                                                item.player.player_challenges_played.aggregate.count;
                                            const opponentChallengesPlayed =
                                                item.player &&
                                                item.player.opponent_challenges_played &&
                                                item.player.opponent_challenges_played.aggregate &&
                                                item.player.opponent_challenges_played.aggregate.count;
                                            const challengesPlayed = playerChallengesPlayed + opponentChallengesPlayed;

                                            const playerChallengesWon =
                                                item.player &&
                                                item.player.player_challenges_won &&
                                                item.player.player_challenges_won.aggregate &&
                                                item.player.player_challenges_won.aggregate.count;
                                            const opponentChallengesWon =
                                                item.player &&
                                                item.player.opponent_challenges_won &&
                                                item.player.opponent_challenges_won.aggregate &&
                                                item.player.opponent_challenges_won.aggregate.count;
                                            const challengesWon = playerChallengesWon + opponentChallengesWon;

                                            const playerChallengesLost =
                                                item.player &&
                                                item.player.player_challenges_lost &&
                                                item.player.player_challenges_lost.aggregate &&
                                                item.player.player_challenges_lost.aggregate.count;
                                            const opponentChallengesLost =
                                                item.player &&
                                                item.player.opponent_challenges_lost &&
                                                item.player.opponent_challenges_lost.aggregate &&
                                                item.player.opponent_challenges_lost.aggregate.count;
                                            const challengesLost = playerChallengesLost + opponentChallengesLost;

                                            return {
                                                ...item,
                                                challenges_played: challengesPlayed,
                                                challenges_won: challengesWon,
                                                challenges_lost: challengesLost,
                                                index: index + 1,
                                                key: index,
                                            };
                                        });

                                    return (
                                        <Table
                                            columns={columns}
                                            dataSource={standings}
                                            pagination={false}
                                            tableLayout="auto"
                                            scroll={{ x: 400 }}
                                            // pagination={{ pageSize: 25 }}
                                            // bordered
                                        />
                                    );
                                }}
                            </Query>
                        </Card>
                    </Col>
                </Row>
            </SectionContent>
        </SectionWrapper>
    );
}
